import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';

import { Select } from '@aline/common';
import { link, params } from '@aline/routes';
import { useNavigate } from 'react-router-dom';
import type { PanelVariableType } from 'src/components';
import { PanelHeader, panelHeaderHeightPx, VariablePanelBody } from 'src/components';
import { useRequiredEditorRuntime } from 'src/editor/editor-runtime-registry';
import { useWalkthroughActive } from 'src/editor/toolbars';
import { useBackToAI } from 'src/editor/toolbars/right-panel/BackToAIButton';
import { useDrawerSearchParam } from 'src/editor/toolbars/right-panel/drawer-search-params';
import { useSelectCurrentUser } from 'src/gql';
import { useDocumentParams } from 'src/helpers';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';
import { makeStyles } from 'tss-react/mui';

import type { VariablePanelState } from './variable-panel-state-store';
import {
  clearVariablePanelState,
  setVariablePanelStateForTabId,
  useVariablePanelStateForTabId,
} from './variable-panel-state-store';

const useStyles = makeStyles()(theme => ({
  addButton: {
    margin: theme.spacing(1.5, 2, 1.5, 0),
  },
  panelContainer: {
    backgroundColor: theme.palette.common.white,
    height: `calc(100% - ${panelHeaderHeightPx}px)`,
    width: '100%',
  },
}));

export type { VariablePanelState };

interface VariablePanelStateContextValue {
  setVariablePanelState: (state: Partial<VariablePanelState>) => void;
  state: VariablePanelState;
}

const VariablePanelStateContext = createContext<VariablePanelStateContextValue | null>(null);

export function useVariablePanelState(): VariablePanelState {
  return useVariablePanelStateContext().state;
}

export function useSetVariablePanelState(): VariablePanelStateContextValue['setVariablePanelState'] {
  return useVariablePanelStateContext().setVariablePanelState;
}

export interface VariablePanelProps {
  documentId: UUID;
  variables: PanelVariableType[];
  loading?: boolean;
}

export function VariablePanelStateProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const { tabId } = useRequiredEditorRuntime();
  const state = useVariablePanelStateForTabId(tabId);
  const setVariablePanelState = useCallback<
    VariablePanelStateContextValue['setVariablePanelState']
  >(newState => setVariablePanelStateForTabId(tabId, newState), [tabId]);
  const stateContextValue = useMemo(() => {
    return { setVariablePanelState, state };
  }, [setVariablePanelState, state]);

  return (
    <VariablePanelStateContext.Provider value={stateContextValue}>
      {children}
    </VariablePanelStateContext.Provider>
  );
}

export function VariablePanel(props: VariablePanelProps): JSX.Element {
  const { classes } = useStyles();
  const { tabId } = useRequiredEditorRuntime();
  const setVariablePanelState = useCallback<
    VariablePanelStateContextValue['setVariablePanelState']
  >(newState => setVariablePanelStateForTabId(tabId, newState), [tabId]);
  const { setValue: setEditFieldId, value: editFieldId } = useDrawerSearchParam(
    params.rightPanel.editFieldId
  );
  const headerText = editFieldId ? 'Edit' : 'Outline';
  const isEditingField = Boolean(editFieldId);
  const handleBackToAI = useBackToAI();

  useEffect(() => {
    clearVariablePanelState(tabId);
  }, [props.documentId, tabId]);

  // Note: call here vs. lower down in component tree so hook doesn't unnecessarily fire (e.g., when switching between panels in VariablePanel).
  useSetFilterOnWalkthrough(setVariablePanelState);

  function goBackToMainPanel(): void {
    setEditFieldId(null);
  }

  return (
    <>
      <PanelHeader
        showBackButton
        backButtonTooltip={!isEditingField ? 'Back to AI' : undefined}
        text={headerText}
        onClickBack={isEditingField ? goBackToMainPanel : handleBackToAI}
      />
      <VariablePanelStateProvider>
        <div
          className={classes.panelContainer}
          data-cy={Select.document.editor.rightDrawer.outline.container}
        >
          <VariablePanelBody {...props} />
        </div>
      </VariablePanelStateProvider>
    </>
  );
}

/**
 * Open the add input panel in the right-hand side of the Editor.
 * Uses URL-based navigation to switch drawer content.
 */
export function useOpenAddInput(): {
  openAddInput: () => void;
} {
  const navigate = useNavigate();
  const { documentId } = useDocumentParams();
  const { openDrawer } = useGlobalDrawer();

  const openAddInput = useCallback(() => {
    navigate(link.DocumentDetailAddInput({ documentId }), { replace: true });
    openDrawer();
  }, [documentId, navigate, openDrawer]);

  return { openAddInput };
}

/**
 * Toggles the variables filter when a user enters/exits walkthrough mode.
 */
function useSetFilterOnWalkthrough(
  setVariablePanelState: VariablePanelStateContextValue['setVariablePanelState']
) {
  const currentUser = useSelectCurrentUser();
  const { walkThroughActive } = useWalkthroughActive();

  useEffect(() => {
    if (!walkThroughActive) setVariablePanelState({ filterItems: [] });
  }, [setVariablePanelState, walkThroughActive]);

  useEffect(() => {
    if (currentUser && walkThroughActive) setVariablePanelState({ filterItems: [currentUser] });
  }, [currentUser, setVariablePanelState, walkThroughActive]);
}

function useVariablePanelStateContext(): VariablePanelStateContextValue {
  const context = useContext(VariablePanelStateContext);
  if (!context) throw new Error('useVariablePanelState must be used within VariablePanel');
  return context;
}
